import { Anchor, Badge, Group, Modal, Stack, Text } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { UnifyTable, type UnifyTableColumn, type UnifyTableMultiFilterConfig } from '@/components/UnifyTable';
import { formatDateTime } from './components/billingFormatters';
import { MOCK_ACKNOWLEDGMENTS, type AcknowledgmentRow } from './mock/mockAcknowledgments';

export function AcknowledgmentsPage(): JSX.Element {
  const [opened, { open, close }] = useDisclosure(false);
  const [selected, setSelected] = useState<AcknowledgmentRow | null>(null);

  const columns = useMemo(
    (): UnifyTableColumn<AcknowledgmentRow>[] => [
      {
        key: 'receivedAt',
        label: 'Received',
        render: (row) => <Text size="sm">{formatDateTime(row.receivedAt)}</Text>,
        getValue: (row) => row.receivedAt,
      },
      {
        key: 'status',
        label: 'Status',
        render: (row) => (
          <Badge variant="light" size="sm" color={row.status === 'accepted' ? 'green' : 'red'}>
            {row.status}
          </Badge>
        ),
        getValue: (row) => row.status,
      },
      {
        key: 'clientName',
        label: 'Client',
        render: (row) => <Text size="sm" fw={600}>{row.clientName}</Text>,
        getValue: (row) => row.clientName,
      },
      {
        key: 'payer',
        label: 'Payer',
        getValue: (row) => row.payer,
      },
      {
        key: 'claimId',
        label: 'Claim',
        render: (row) => (
          <Anchor
            component={Link}
            to={`/dashboard/billing/claims/${encodeURIComponent(row.claimId)}`}
            size="sm"
            onClick={(e) => e.stopPropagation()}
          >
            {row.claimId}
          </Anchor>
        ),
        getValue: (row) => row.claimId,
      },
      {
        key: 'rejectionCodes',
        label: 'Codes',
        render: (row) => (
          <Text size="sm" c="dimmed">
            {row.rejectionCodes.length > 0 ? row.rejectionCodes.join(', ') : '—'}
          </Text>
        ),
        getValue: (row) => row.rejectionCodes.join(', '),
      },
    ],
    [],
  );

  const multiFilters = useMemo(
    (): UnifyTableMultiFilterConfig[] => [
      {
        columnKey: 'status',
        allLabel: 'All statuses',
        placeholder: 'Status',
        data: ['accepted', 'rejected'],
        emptyMeansAll: true,
      },
    ],
    [],
  );

  return (
    <>
      <UnifyTable
        title="Acknowledgments"
        subtitle="277CA payer responses"
        columns={columns}
        data={MOCK_ACKNOWLEDGMENTS}
        getRowKey={(row) => row.id}
        searchable
        searchPlaceholder="Search by client, payer, claim…"
        sortable
        pagination
        defaultRowsPerPage={10}
        multiFilters={multiFilters}
        onRowClick={(row) => {
          setSelected(row);
          open();
        }}
      />

      <Modal opened={opened} onClose={close} title="277CA Disposition" size="md" centered>
        {selected && (
          <Stack gap="md">
            <Group gap="xs">
              <Badge variant="light" color={selected.status === 'accepted' ? 'green' : 'red'}>
                {selected.status}
              </Badge>
              <Text size="sm" c="dimmed">
                {formatDateTime(selected.receivedAt)}
              </Text>
            </Group>
            <Text size="sm">{selected.disposition}</Text>
            <Anchor
              component={Link}
              to={`/dashboard/billing/claims/${encodeURIComponent(selected.claimId)}`}
              size="sm"
              fw={600}
            >
              View claim {selected.claimId}
            </Anchor>
          </Stack>
        )}
      </Modal>
    </>
  );
}
